import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { GroupParams } from '../../api'
import { t } from '../../i18n'
import { Button, Check, Field, Input, OptionList, type Option } from '../../ui'
import { NODES_KEY, fetchNodes } from '../Nodes'
import { GroupDialog } from './GroupDialog'
import { DEFAULT_GROUP_KEY, OTHER_REGION, adminErrorText, groupAdminApi, ingestZone, takenIndexes, useDefaultGroup } from './groupAdmin'
import { groupPath } from './groups'
import { FAILOVER_KEY, useFailover } from './NodeFailover'
import { tn } from './nodeSwitch'
import { EgressModeOptions } from './GroupEgress'

/*
 * Create node group (GROUP-UI-3, NG-12 A): one ConfirmDialog level 3 — region, index, the name
 * as it will be seen (read-only, mono), the group's settings and the code. Region: an option list
 * of the nodes' regions (≤ 6, NG-13) with "Other region" for a new one; more than 6 → a select
 * (TrackSelection: > 6 choices). Index 1–9: a select without the taken ones and a line saying which
 * exist. POST /api/admin/failover/groups {kind: group, region, index, params, totp}.
 */

const REGION_RE = /^[a-z]{2}-[a-z]{2,16}$/
const DEFAULTS = { auto_weights: true, auto_ramp: true, ramp_s: 600, drain_min: 60, egress_mode: 'center' as 'center' | 'group' }

export function CreateGroupButton({ className = '' }: { className?: string }) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLElement | null>(null)
  return (
    <>
      <Button data-testid="group-create" className={`max-md:min-h-[44px] ${className}`} onClick={(e) => ((trigger.current = e.currentTarget), setOpen(true))}>
        {t('gx.create.btn')}
      </Button>
      {open && <CreateGroupDialog trigger={trigger} onClose={() => setOpen(false)} />}
    </>
  )
}

export function CreateGroupDialog({ trigger, onClose }: { trigger: RefObject<HTMLElement | null>; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const nodes = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes })
  const fo = useFailover()
  const dg = useDefaultGroup()
  const groups = useMemo(() => fo.data?.groups ?? [], [fo.data])
  const regions = useMemo(() => {
    const c = new Map<string, number>()
    for (const n of nodes.data ?? []) {
      if (n.status === 'revoked' || n.roles?.includes('integrations') || !REGION_RE.test(n.region)) continue
      c.set(n.region, (c.get(n.region) ?? 0) + 1)
    }
    return [...c.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [nodes.data])

  const [pick, setPick] = useState<string>('')
  const [other, setOther] = useState('')
  const [otherTouched, setOtherTouched] = useState(false)
  // the first region of the nodes is the default choice once they arrive
  useEffect(() => {
    if (!pick && nodes.data) setPick(regions[0]?.[0] ?? OTHER_REGION)
  }, [pick, nodes.data, regions])
  const region = pick === OTHER_REGION ? other.trim().toLowerCase() : pick
  const regionOk = REGION_RE.test(region)
  const taken = takenIndexes(groups, region)
  const free = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((i) => !taken.includes(i))
  const [index, setIndex] = useState(1)
  // a new region starts at its lowest free index; a taken one moves there too
  useEffect(() => {
    if (free.length) setIndex(free[0]!)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, taken.join(',')])

  const [aw, setAw] = useState(DEFAULTS.auto_weights)
  const [ar, setAr] = useState(DEFAULTS.auto_ramp)
  const [mode, setMode] = useState(DEFAULTS.egress_mode)
  const [ramp, setRamp] = useState(String(DEFAULTS.ramp_s))
  const [drain, setDrain] = useState(String(DEFAULTS.drain_min))
  const rampN = Number(ramp)
  const drainN = Number(drain)
  const rampOk = Number.isInteger(rampN) && rampN >= 60 && rampN <= 3600
  const drainOk = Number.isInteger(drainN) && drainN >= 1 && drainN <= 1440

  const name = regionOk && free.includes(index) ? `${region}-${index}` : ''
  const zone = ingestZone(groups, dg.data)
  const fqdn = name && zone ? `${name}.${zone}` : name
  const ready = !!name && rampOk && drainOk

  const regionOpts: Option<string>[] = [
    ...regions.map(([r, n]) => ({ value: r, label: <span className="sx-mono">{r}</span>, right: t(n === 1 ? 'gx.create.nodes.one' : 'gx.create.nodes.many', { n }), testId: `group-create-region-${r}` })),
    { value: OTHER_REGION, label: t('gx.create.other'), testId: 'group-create-region-other' },
  ]
  const indexHelp = !regionOk
    ? t('gx.create.indexHelp')
    : free.length === 0
      ? tn('gx.create.allTaken', { region })
      : taken.length
        ? tn('gx.create.taken', { list: taken.sort().map((i) => `${region}-${i}`).join(', ') })
        : t('gx.create.indexHelp')

  return (
    <GroupDialog
      testId="group-create"
      title={t('gx.create.title')}
      action={t('gx.create.btn')}
      busyLabel={t('gx.create.busy')}
      primary
      ready={ready}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => adminErrorText(e, { name })}
      submit={async (totp) => {
        const params: Partial<GroupParams> = { auto_weights: aw, auto_ramp: ar, ramp_s: rampN, drain_deadline_s: drainN * 60, egress_mode: mode }
        const g = await groupAdminApi.create({ region, index, params, totp })
        await Promise.all([qc.invalidateQueries({ queryKey: FAILOVER_KEY }), qc.invalidateQueries({ queryKey: DEFAULT_GROUP_KEY })])
        onClose()
        navigate(groupPath(g.id), { state: { done: t('gx.create.done', { name: g.name }) } })
      }}
    >
      <div className="sx-field">
        <span className="lbl" id="group-create-region-lbl">{t('gx.create.region')}</span>
        {regions.length <= 6 ? (
          <OptionList value={pick} options={regionOpts} onChange={setPick} labelledBy="group-create-region-lbl" />
        ) : (
          <select className="sx-select w-full focus:border-accent focus:shadow-focus focus:outline-none" aria-labelledby="group-create-region-lbl" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="group-create-region">
            {regions.map(([r]) => (
              <option key={r} value={r}>{r}</option>
            ))}
            <option value={OTHER_REGION}>{t('gx.create.other')}</option>
          </select>
        )}
        <span className="help">{t('gx.create.regionHelp')}</span>
      </div>
      {pick === OTHER_REGION && (
        <Field id="group-create-other" label={t('gx.create.otherLabel')} help={t('gx.create.otherHelp')} error={otherTouched && other && !regionOk ? t('gx.create.format') : undefined}>
          <Input id="group-create-other" value={other} placeholder="eu-central" spellCheck={false} autoComplete="off" maxLength={19} onBlur={() => setOtherTouched(true)} onChange={(e) => setOther(e.target.value)} data-testid="group-create-other" />
        </Field>
      )}
      <Field id="group-create-index" label={t('gx.create.index')} help={indexHelp}>
        <select id="group-create-index" className="sx-select max-w-[160px] font-mono focus:border-accent focus:shadow-focus focus:outline-none" value={index} disabled={!free.length} onChange={(e) => setIndex(Number(e.target.value))} data-testid="group-create-index">
          {free.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
      </Field>
      <div className="sx-field" data-testid="group-create-name">
        <span className="lbl">{t('gx.create.name')}</span>
        <span className="sx-combo">
          <span className={`v truncate ${name ? '' : 'text-muted'}`} title={fqdn}>{fqdn || '—'}</span>
        </span>
        <span className="help">{name && !zone ? t('gx.create.noZone') : t('gx.create.nameHelp')}</span>
      </div>
      <span className="sx-label">{t('group.settings')}</span>
      <Check checked={aw} onChange={setAw} label={t('group.set.autoWeights')} hint={t('group.set.autoWeightsHint')} testId="group-create-auto-weights" />
      <Check checked={ar} onChange={setAr} label={t('group.set.autoRamp')} hint={t('group.set.autoRampHint')} testId="group-create-auto-ramp" />
      <div className="grid grid-cols-2 gap-3">
        <Field id="group-create-ramp" label={t('group.set.ramp')} help={t('gx.create.rampHelp')} error={rampOk ? undefined : t('group.err.range')}>
          <Input id="group-create-ramp" inputMode="numeric" value={ramp} disabled={!ar} onChange={(e) => setRamp(e.target.value.replace(/\D/g, ''))} data-testid="group-create-ramp" />
        </Field>
        <Field id="group-create-drain" label={t('group.set.drain')} help={t('gx.create.drainHelp')} error={drainOk ? undefined : t('group.err.range')}>
          <Input id="group-create-drain" inputMode="numeric" value={drain} onChange={(e) => setDrain(e.target.value.replace(/\D/g, ''))} data-testid="group-create-drain" />
        </Field>
      </div>
      {/* GEU-08 B: the same option list as the group's settings; a new group has no outputs node yet */}
      <EgressModeOptions value={mode} onChange={setMode} nodes={0} />
      {name && <p className="m-0 text-[13px] text-text-soft" data-testid="group-create-then">{tn('gx.create.then', { name })}</p>}
    </GroupDialog>
  )
}
